import { Info, TrendingUp } from "lucide-react";
import React from "react";
import { useTranslation } from "../../../../contexts/LanguageContext";
import { useAds } from "../../hooks/AdsContext";

const PredictedResultsCard: React.FC = () => {
  const {
    reachEstimate,
    loadingEstimate,
    reachEstimateError,
    dailyBudget,
    endDate,
    startDate,
    platform,
  } = useAds();
  const { t } = useTranslation();

  const getDurationInDays = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end.getTime() - start.getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 3600 * 24)));
  };

  const duration = getDurationInDays();
  const budgetNum = parseFloat(dailyBudget.replace(",", ".")) || 0;
  const isLinkedIn = platform === "linkedin";

  return (
    <section
      className={`bg-white rounded-2xl p-4 border ${isLinkedIn ? "border-[#0077b5]/20 shadow-xs" : "border-purple-100 shadow-xs"} animate-in fade-in duration-300 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4`}
    >
      {/* Header block */}
      <div className="flex items-start gap-2 lg:shrink-0 lg:w-52">
        <div
          className={`w-8 h-8 shrink-0 flex items-center justify-center ${isLinkedIn ? "bg-[#0077b5]" : "bg-purple-600"} rounded-lg text-white shadow-xs`}
        >
          <TrendingUp className="w-5 h-5" />
        </div>
        <div className="h-8 flex flex-col justify-between">
          <h2 className="text-xs font-bold text-gray-900 tracking-tight uppercase leading-none">
            {t("Resultados Previstos")}
          </h2>
          <span className="text-[10px] text-gray-400 font-medium leading-none">
            {t("{duration} dias de veiculação", { duration })}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div
        className={`flex-1 min-w-0 grid grid-cols-3 gap-1.5 sm:gap-2 lg:border-l lg:pl-4 ${isLinkedIn ? "lg:border-blue-50" : "lg:border-purple-50"}`}
      >
        <div
          className={`px-2.5 py-1.5 rounded-lg ${isLinkedIn ? "bg-blue-50/40" : "bg-purple-50/20"}`}
        >
          <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
            {t("Público")}
            <Info
              className={`w-2.5 h-2.5 ${isLinkedIn ? "text-[#0077b5]" : "text-purple-400"}`}
            />
          </div>
          <div className="text-sm font-black text-gray-900 tracking-tight truncate">
            {loadingEstimate ? (
              <span className="text-xs text-gray-400">...</span>
            ) : isLinkedIn && reachEstimateError ? (
              <span className="text-xs text-gray-400 italic">
                {t("Indisponível")}
              </span>
            ) : reachEstimate?.audienceSize ? (
              `${(reachEstimate.audienceSize / 1000).toFixed(0)}k+`
            ) : (
              "580.000+"
            )}
          </div>
        </div>

        <div className="px-2.5 py-1.5 bg-gray-50 rounded-lg">
          <div className="text-[9px] font-bold text-gray-400 uppercase">
            {t("Investido")}
          </div>
          <div className="text-sm font-bold text-gray-900 truncate">
            R${" "}
            {dailyBudget
              ? (budgetNum * duration).toLocaleString("pt-BR")
              : "1.400"}
          </div>
        </div>

        <div className="px-2.5 py-1.5 bg-gray-50 rounded-lg">
          <div className="text-[9px] font-bold text-gray-400 uppercase">
            {t("Impressões")}
          </div>
          <div className="text-xs font-bold text-gray-900 mt-0.5 truncate">
            {loadingEstimate ? (
              "..."
            ) : isLinkedIn && reachEstimateError ? (
              <span className="text-gray-400 italic font-normal">
                {t("Indisponível")}
              </span>
            ) : reachEstimate?.impressions?.length === 2 ? (
              `${reachEstimate.impressions[0]} - ${reachEstimate.impressions[1]}`
            ) : (
              `${Math.floor(budgetNum * duration * 8).toLocaleString("pt-BR")} - ${Math.floor(budgetNum * duration * 35).toLocaleString("pt-BR")}`
            )}
          </div>
        </div>
      </div>

      <p
        className="hidden lg:block text-[10px] text-gray-400 leading-relaxed font-medium lg:w-40 lg:shrink-0 lg:pl-3 lg:border-l border-gray-100"
        title={t(
          "Estimativa baseada em dados históricos para os {duration} dias do período.",
          { duration },
        )}
      >
        {t("Estimativa baseada em dados históricos.")}
      </p>
    </section>
  );
};

export default PredictedResultsCard;
